import { DateTime } from 'ts-luxon';
import React from 'react';
import { hasTokenExpired, useAuthentication } from './useAuthentication';

const REFRESH_LEAD_MS = 60_000; // Refresh 1 minute before expiry
const RETRY_DELAY_MS = 10_000; // Wait before retrying after a failed refresh

// Login answers a bad password with a 400. Anything else (server down, timeout) is worth retrying.
const isLoginRejected = (error: unknown) => (error as { status?: number } | undefined)?.status === 400;

// Returns true while a token that had already expired when the page loaded is being replaced.
export const useAuthTokenUpdater = () => {
    const { login, logout, credentials, authorization } = useAuthentication();

    // Keep the latest login/credentials without re-running the scheduler.
    // login gets a new identity on every render, so depending on it would
    // reschedule (and immediately re-fire) on each failed refresh.
    const loginRef = React.useRef(login);
    const credentialsRef = React.useRef(credentials);
    React.useEffect(() => {
        loginRef.current = login;
        credentialsRef.current = credentials;
    });

    const expiresAt = authorization?.expiresAt;
    const hasCredentials = Boolean(credentials?.password);

    const [renewingExpiredToken, setRenewingExpiredToken] = React.useState(
        () => Boolean(expiresAt) && hasTokenExpired(expiresAt)
    );

    //Auto refresh token before it expires
    React.useEffect(() => {
        if (!expiresAt) return;
        if (!hasCredentials) {
            if (hasTokenExpired(expiresAt)) logout();
            return;
        }

        let cancelled = false;
        let timer: ReturnType<typeof setTimeout>;

        const scheduleRefresh = () => {
            const msUntilExpiry = DateTime.fromISO(expiresAt).diffNow().milliseconds;
            // Clamp to 0 so an already-expired token refreshes soon, not with a negative (immediate) delay
            const delay = Math.max(msUntilExpiry - REFRESH_LEAD_MS, 0);
            console.warn(`Token refresh scheduled in ${Math.round(delay / 1000)}s`);

            timer = setTimeout(async () => {
                const creds = credentialsRef.current;
                if (cancelled || !creds) return;
                try {
                    console.log('Refreshing token...');
                    await loginRef.current(creds);
                    setRenewingExpiredToken(false);
                    // On success authorization changes, which re-runs this effect and reschedules
                } catch (error) {
                    if (cancelled) return;
                    if (isLoginRejected(error)) {
                        console.warn('Token refresh rejected; logging out');
                        logout();
                        return;
                    }
                    console.warn(`Token refresh failed; retrying in ${RETRY_DELAY_MS / 1000}s`);
                    timer = setTimeout(scheduleRefresh, RETRY_DELAY_MS);
                }
            }, delay);
        };

        scheduleRefresh();

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [expiresAt, hasCredentials, logout]);

    return renewingExpiredToken;
};
